import { isEmpty, isFinite, isNil, isString } from 'lodash';
import { API } from '@shared/types/api';
import { CardMatchLookups } from './cardMatchLookups';
import { KEYWORD_MAX_LENGTH, getFieldValue, getTime, normalizeKeyword } from './indexedValues';

export type CardComparator = (a: API.WorkflowEntity, b: API.WorkflowEntity) => number;

// Server-managed components no edit can change, so a card keeps its place in a list sorted on them.
const IMMUTABLE_SORT_COMPONENT_IDS = ['createdAt', 'sequenceNumber'];

/**
 * Orders cards the way core-api's search orders a list query's `sortByComponents`, ending on the
 * card id as it does. `undefined` when the order can't be reproduced here: a query with no sort is
 * ordered by its view's own script, and core-api only sorts on number, date and text components.
 */
export function getCardComparator(
  sorts: API.SortByComponent[] | null | undefined,
  workflowTemplateId: string,
  lookups: CardMatchLookups,
): CardComparator | undefined {
  if (isNil(sorts) || isEmpty(sorts)) {
    return undefined;
  }
  const comparators = sorts.map(sort => {
    const component = lookups.getComponent(workflowTemplateId, sort.componentId);
    return isNil(component) ? undefined : getSortComparator(sort, component.type);
  });
  if (comparators.some(isNil)) {
    return undefined;
  }
  return (a, b) => {
    for (const comparator of comparators) {
      const order = comparator!(a, b);
      if (order !== 0) {
        return order;
      }
    }
    return compareValues(a.id, b.id);
  };
}

/** Whether every sort is on a value no edit can change, so a card never moves within the list. */
export function hasImmutableSort(sorts: API.SortByComponent[] | null | undefined): boolean {
  return (
    !isNil(sorts) &&
    !isEmpty(sorts) &&
    sorts.every(sort => IMMUTABLE_SORT_COMPONENT_IDS.includes(sort.componentId))
  );
}

function getSortComparator(
  { componentId, direction }: API.SortByComponent,
  componentType: string,
): CardComparator | undefined {
  const order = direction === 'DESC' ? -1 : 1;
  switch (componentType) {
    // A number or date with no value sorts first both ways: core-api stands in the lowest safe
    // integer when ascending and the highest when descending.
    case 'NUMBER':
    case 'DATE': {
      const missing = direction === 'DESC' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      const getValue = (card: API.WorkflowEntity) => {
        const fieldValue = getFieldValue(card, componentId);
        const value = componentType === 'NUMBER' ? fieldValue : getDateValue(fieldValue);
        return isFinite(value) ? (value as number) : missing;
      };
      return (a, b) => order * compareValues(getValue(a), getValue(b));
    }
    // Text with no value sorts first ascending and last descending - lowest both ways.
    case 'TEXT': {
      const getValue = (card: API.WorkflowEntity) => {
        const fieldValue = getFieldValue(card, componentId);
        return isString(fieldValue) && fieldValue.length <= KEYWORD_MAX_LENGTH
          ? normalizeKeyword(fieldValue)
          : undefined;
      };
      return (a, b) => order * compareWithMissingLowest(getValue(a), getValue(b));
    }
    default:
      return undefined;
  }
}

function getDateValue(fieldValue: unknown): number | undefined {
  return isString(fieldValue) ? getTime(fieldValue) : undefined;
}

function compareWithMissingLowest(a: string | undefined, b: string | undefined): number {
  if (isNil(a) || isNil(b)) {
    return Number(!isNil(a)) - Number(!isNil(b));
  }
  return compareValues(a, b);
}

function compareValues<Value extends string | number>(a: Value, b: Value): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}
